import React, {useState} from 'react';
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Container,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	IconButton,
	List,
	ListItem,
	ListItemText,
	Paper,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import dayjs from 'dayjs';
import config from '../common/config';
import useAuth from '../components/Auth';
import {
	ApiKeyRecord,
	CreatedApiKeyRecord,
	useApiKeyCreateMutation,
	useApiKeyDeleteMutation,
	useApiKeysFindQuery,
} from '../services/apiKeys';

const MCP_SERVER_URL = `${config.remoteApiAccountsUrl}/mcp`;

function CopyField({label, value}: {label: string, value: string}) {
	const [isCopied, setIsCopied] = useState(false);

	const onCopy = async () => {
		await navigator.clipboard.writeText(value);
		setIsCopied(true);
		setTimeout(() => setIsCopied(false), 2000);
	};

	return (
		<Box display='flex' alignItems='flex-start' gap={1}>
			<TextField
				label={label}
				value={value}
				fullWidth
				size='small'
				InputProps={{readOnly: true, sx: {fontFamily: 'monospace', fontSize: 13}}}
			/>
			<Tooltip title={isCopied ? 'Copied' : 'Copy'} disableInteractive>
				<IconButton onClick={onCopy} aria-label={`Copy ${label}`}>
					<ContentCopyIcon fontSize='small' />
				</IconButton>
			</Tooltip>
		</Box>
	);
}

function ApiKeysView() {
	const {isAuthenticated} = useAuth();
	const {data: apiKeys = [], isLoading} = useApiKeysFindQuery(undefined, {skip: !isAuthenticated});
	const [createApiKey, {isLoading: isCreating}] = useApiKeyCreateMutation();
	const [deleteApiKey, {isLoading: isDeleting}] = useApiKeyDeleteMutation();
	const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
	const [newKeyName, setNewKeyName] = useState('');
	const [createdApiKey, setCreatedApiKey] = useState<CreatedApiKeyRecord | null>(null);
	const [apiKeyToDelete, setApiKeyToDelete] = useState<ApiKeyRecord | null>(null);

	const openCreateDialog = () => {
		setNewKeyName('');
		setCreatedApiKey(null);
		setIsCreateDialogOpen(true);
	};

	const closeCreateDialog = () => {
		if (!isCreating) {
			setIsCreateDialogOpen(false);
			setCreatedApiKey(null);
		}
	};

	const onCreate = async () => {
		const name = newKeyName.trim();
		if (!name) {
			return;
		}
		setCreatedApiKey(await createApiKey({name}).unwrap());
	};

	const onDeleteConfirm = async () => {
		if (!apiKeyToDelete) {
			return;
		}
		await deleteApiKey({id: apiKeyToDelete.id}).unwrap();
		setApiKeyToDelete(null);
	};

	if (!isAuthenticated) {
		return (
			<Container maxWidth='sm'>
				<Typography variant='h4' component='h1' sx={{mt: 2, mb: 3}}>
					API keys
				</Typography>
				<Typography variant='body2' color='text.secondary'>
					Sign in to create API keys for your cloud workspace.
				</Typography>
			</Container>
		);
	}

	return (
		<Container maxWidth='sm' sx={{mb: 5}}>
			<Typography variant='h4' component='h1' sx={{mt: 2, mb: 2}}>
				API keys
			</Typography>
			<Typography variant='body2' color='text.secondary' sx={{mb: 3}}>
				Connect LLM agents to your workspace through MCP. An agent using one of these keys can read your collections, their entries, and your canvas notes. Treat every key like a password.
			</Typography>
			<Box mb={4}>
				<CopyField label='MCP server URL' value={MCP_SERVER_URL} />
			</Box>
			{isLoading ? (
				<Box display='flex' justifyContent='center' py={3}>
					<CircularProgress size={32} />
				</Box>
			) : apiKeys.length === 0 ? (
				<Typography variant='body2' color='text.secondary'>
					No API keys yet.
				</Typography>
			) : (
				<List disablePadding sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
					{apiKeys.map((apiKey) => (
						<ListItem
							component={Paper}
							key={apiKey.id}
							sx={{
								backgroundColor: 'transparent',
								boxShadow: '0 2px 4px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.1)',
							}}
							secondaryAction={
								<Tooltip title='Revoke key' disableInteractive>
									<IconButton onClick={() => setApiKeyToDelete(apiKey)} aria-label='Revoke key'>
										<DeleteOutlineIcon fontSize='small' />
									</IconButton>
								</Tooltip>
							}
						>
							<ListItemText
								primary={apiKey.name}
								secondary={`${apiKey.keyPrefix}… · Created ${dayjs(apiKey.createdAt).format('D MMM YYYY')}`}
							/>
						</ListItem>
					))}
				</List>
			)}
			<Box display='flex' mt={1}>
				<Button size='medium' color='primary' onClick={openCreateDialog} startIcon={<AddIcon />}>
					New API key
				</Button>
			</Box>
			<Dialog open={isCreateDialogOpen} onClose={closeCreateDialog} maxWidth='sm' fullWidth>
				{createdApiKey ? (
					<>
						<DialogTitle>API key created</DialogTitle>
						<DialogContent>
							<Alert severity='warning' sx={{mb: 2}}>
								Copy this key now. You won&apos;t be able to see it again.
							</Alert>
							<CopyField label='API key' value={createdApiKey.key} />
						</DialogContent>
						<DialogActions sx={{px: 3, pb: 2}}>
							<Button variant='contained' onClick={closeCreateDialog}>Done</Button>
						</DialogActions>
					</>
				) : (
					<form onSubmit={(event) => {
						event.preventDefault();
						onCreate();
					}}>
						<DialogTitle>New API key</DialogTitle>
						<DialogContent>
							<TextField
								autoFocus
								margin='dense'
								label='Name'
								placeholder='e.g. My laptop agent'
								fullWidth
								value={newKeyName}
								onChange={(event) => setNewKeyName(event.target.value)}
								disabled={isCreating}
								inputProps={{maxLength: 100}}
							/>
						</DialogContent>
						<DialogActions sx={{px: 3, pb: 2}}>
							<Button onClick={closeCreateDialog} disabled={isCreating}>Cancel</Button>
							<Button type='submit' variant='contained' disabled={isCreating || !newKeyName.trim()}>
								{isCreating ? <CircularProgress size={20} color='inherit' /> : 'Create'}
							</Button>
						</DialogActions>
					</form>
				)}
			</Dialog>
			<Dialog open={Boolean(apiKeyToDelete)} onClose={() => !isDeleting && setApiKeyToDelete(null)} maxWidth='xs' fullWidth>
				<DialogTitle>Revoke API key?</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Agents using &quot;{apiKeyToDelete?.name}&quot; will immediately lose access to your workspace.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setApiKeyToDelete(null)} disabled={isDeleting}>Cancel</Button>
					<Button color='error' variant='contained' onClick={onDeleteConfirm} disabled={isDeleting}>
						Revoke
					</Button>
				</DialogActions>
			</Dialog>
		</Container>
	);
}

export default ApiKeysView;
